import React, { useEffect, useState, useMemo } from 'react';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  getFirestore,
} from 'firebase/firestore';
import {
  Box,
  Container,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

export default function ManageUsers() {
  const db = getFirestore();
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // For role-change or password reset confirmation
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [newRole, setNewRole] = useState('');

  // For password reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetTargetEmail, setResetTargetEmail] = useState('');

  // For changing the user's "Square ID" (matching them to a member)
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [memberSearchTerm, setMemberSearchTerm] = useState(''); // for searching members

  // MAIN user search
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // -----------------------------
  // 1) Fetch all user docs
  // -----------------------------
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(db, 'users'));
        const allUsers = snap.docs.map((docSnap) => ({
          id: docSnap.id, // firebase UID
          ...docSnap.data(),
        }));
        setUsers(allUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
      setLoading(false);
    };
    fetchAllUsers();
  }, [db]);

  // 2) Fetch all members (Square ID docs) so we can pick from them
  useEffect(() => {
    const fetchAllMembers = async () => {
      try {
        const membersSnap = await getDocs(collection(db, 'members'));
        const all = membersSnap.docs.map((d) => ({
          id: d.id, // the doc ID = the 'squareCustomerId'
          ...d.data(),
        }));
        setAllMembers(all);
      } catch (error) {
        console.error('Error fetching members:', error);
      }
    };
    fetchAllMembers();
  }, [db]);

  // -----------------------------
  // 3) Searching: Filter users
  // -----------------------------
  // We filter the 'users' array based on userSearchTerm. We'll match
  // email, firstName, lastName, uid, etc.
  const filteredUsers = useMemo(() => {
    if (!userSearchTerm) return users;
    const lower = userSearchTerm.toLowerCase();
    return users.filter((u) => {
      const { email = '', firstName = '', lastName = '', uid = '' } = u;
      return (
        email.toLowerCase().includes(lower) ||
        firstName.toLowerCase().includes(lower) ||
        lastName.toLowerCase().includes(lower) ||
        uid.toLowerCase().includes(lower)
      );
    });
  }, [users, userSearchTerm]);

  // -----------------------------
  // 4) Searching: Filter members
  // -----------------------------
  // In the "Change Member" dialog, we also filter allMembers by firstName, lastName, or email.
  const filteredMembers = useMemo(() => {
    if (!memberSearchTerm) return allMembers;
    const lower = memberSearchTerm.toLowerCase();
    return allMembers.filter((m) => {
      const { firstName = '', lastName = '', email = '' } = m;
      return (
        firstName.toLowerCase().includes(lower) ||
        lastName.toLowerCase().includes(lower) ||
        email.toLowerCase().includes(lower) ||
        m.id.toLowerCase().includes(lower)
      );
    });
  }, [allMembers, memberSearchTerm]);

  // -----------------------------
  // 5) Handling role changes
  // -----------------------------
  const handleRoleChangeClick = (user) => {
    setTargetUser(user);
    setNewRole(user.role || 'member');
    setRoleDialogOpen(true);
  };

  const confirmRoleChange = async () => {
    if (!targetUser) return;
    try {
      const userRef = doc(db, 'users', targetUser.id);
      await updateDoc(userRef, { role: newRole });

      alert(`Role updated to "${newRole}" for ${targetUser.email}.`);
      setRoleDialogOpen(false);

      // Update local state if you want
      setUsers((prev) =>
        prev.map((u) =>
          u.id === targetUser.id ? { ...u, role: newRole } : u
        )
      );
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role.');
    }
  };

  const cancelRoleChange = () => {
    setRoleDialogOpen(false);
    setTargetUser(null);
    setNewRole('');
  };

  // -----------------------------
  // 6) Handling password resets
  // -----------------------------
  const handleResetClick = (user) => {
    setResetTargetEmail(user.email);
    setResetDialogOpen(true);
  };

  const confirmSendReset = async () => {
    if (!resetTargetEmail) return;
    try {
      await sendPasswordResetEmail(auth, resetTargetEmail);
      alert(`Password reset link sent to ${resetTargetEmail}.`);
      setResetDialogOpen(false);
    } catch (err) {
      console.error('Error sending reset email:', err);
      alert(`Failed to send reset email: ${err.message}`);
    }
  };

  const cancelResetDialog = () => {
    setResetDialogOpen(false);
    setResetTargetEmail('');
  };

  // -----------------------------
  // 7) Handling "Change Member" (Square ID)
  // -----------------------------
  const handleChangeMemberClick = (user) => {
    setTargetUser(user);

    // Check if there's a direct "member" doc that matches user.email.
    // If found, default to that; otherwise blank.
    const matchedMember = allMembers.find(
      (m) => m.email && m.email.toLowerCase() === user.email?.toLowerCase()
    );
    if (matchedMember) {
      setSelectedMemberId(matchedMember.id);
    } else {
      // if no match, you can either keep user.squareCustomerId or blank
      setSelectedMemberId('');
    }

    setMemberDialogOpen(true);
  };

  const confirmMemberChange = async () => {
    if (!targetUser) return;
    try {
      const userRef = doc(db, 'users', targetUser.id);
      await updateDoc(userRef, {
        squareCustomerId: selectedMemberId,
        memberDocId: selectedMemberId,
      });

      alert(`Square ID changed to "${selectedMemberId}" for ${targetUser.email}.`);
      setMemberDialogOpen(false);

      // update local state
      setUsers((prev) =>
        prev.map((u) =>
          u.id === targetUser.id
            ? { ...u, squareCustomerId: selectedMemberId, memberDocId: selectedMemberId }
            : u
        )
      );
    } catch (err) {
      console.error('Error updating squareCustomerId:', err);
      alert('Failed to update Square ID.');
    }
  };

  const cancelMemberChange = () => {
    setMemberDialogOpen(false);
    setTargetUser(null);
    setSelectedMemberId('');
  };

  // -----------------------------
  // 8) Render UI
  // -----------------------------
  const renderUsersTable = () => {
    if (filteredUsers.length === 0) {
      return <Typography>No users found with that search.</Typography>;
    }
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>First Name</TableCell>
            <TableCell>Last Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>UID</TableCell>
            <TableCell>Square ID</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredUsers.map((u) => (
            <TableRow key={u.id}>
              <TableCell>{u.email}</TableCell>
              <TableCell>{u.firstName}</TableCell>
              <TableCell>{u.lastName}</TableCell>
              <TableCell>{u.role}</TableCell>
              <TableCell>{u.uid}</TableCell>
              <TableCell>{u.squareCustomerId || 'N/A'}</TableCell>
              <TableCell>
                {/* 1) Change role */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleRoleChangeClick(u)}
                  sx={{ mr: 1, mb: 1 }}
                >
                  Change Role
                </Button>
                {/* 2) Send pass reset */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleResetClick(u)}
                  sx={{ mr: 1, mb: 1 }}
                >
                  Reset Password
                </Button>
                {/* 3) Change Member (Square ID) */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleChangeMemberClick(u)}
                >
                  Change Square ID
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>

      {/* MAIN search for users */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Search users..."
          variant="outlined"
          fullWidth
          value={userSearchTerm}
          onChange={(e) => setUserSearchTerm(e.target.value)}
        />
      </Box>

      {loading ? (
        <Typography>Loading users...</Typography>
      ) : (
        renderUsersTable()
      )}

      {/* Dialog to change role */}
      <Dialog open={roleDialogOpen} onClose={cancelRoleChange}>
        <DialogTitle>Change User Role</DialogTitle>
        <DialogContent>
          {targetUser && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Assign a new role to <strong>{targetUser.email}</strong>.
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  label="Role"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <MenuItem value="member">member</MenuItem>
                  <MenuItem value="staff">staff</MenuItem>
                  <MenuItem value="admin">admin</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRoleChange}>Cancel</Button>
          <Button variant="contained" onClick={confirmRoleChange}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to confirm password reset */}
      <Dialog open={resetDialogOpen} onClose={cancelResetDialog}>
        <DialogTitle>Send Password Reset?</DialogTitle>
        <DialogContent>
          <Typography>
            Send a password reset link to <strong>{resetTargetEmail}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelResetDialog}>No</Button>
          <Button variant="contained" onClick={confirmSendReset}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog to change the user’s Square ID (memberDocId) */}
      <Dialog open={memberDialogOpen} onClose={cancelMemberChange} maxWidth="sm" fullWidth>
        <DialogTitle>Change Member (Square ID)</DialogTitle>
        <DialogContent>
          {targetUser && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Assign a new Square ID to <strong>{targetUser.email}</strong>.
              </Typography>

              {/* Search box for members */}
              <TextField
                label="Search members..."
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
                value={memberSearchTerm}
                onChange={(e) => setMemberSearchTerm(e.target.value)}
              />

              <FormControl fullWidth>
                <InputLabel id="member-select-label">Members (Square IDs)</InputLabel>
                <Select
                  labelId="member-select-label"
                  label="Members (Square IDs)"
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                >
                  {filteredMembers.map((m) => {
                    const first = m.firstName || '';
                    const last = m.lastName || '';
                    const em = m.email || '';
                    // E.g. "John Smith (john@test.com) - ABC123"
                    const display = `${first} ${last} (${em}) - ${m.id}`;
                    return (
                      <MenuItem key={m.id} value={m.id}>
                        {display}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelMemberChange}>Cancel</Button>
          <Button variant="contained" onClick={confirmMemberChange}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
